class CustomAvisList extends HTMLElement {
  constructor() {
    super();
    this.currentPage = this.getPageFromUrl() || 1;
    this.itemsPerPage = 24;
    this.totalItems = 0;
    this.isLoading = false;
    this.searchTerm = '';
    this.allAvisItems = [];
    this.filteredAvisItems = [];
    this.abortController = null;
    this.pagination = {
      currentPage: 1,
      pageSize: this.itemsPerPage,
      hasNextPage: false
    };
    this.resizeRAF = null;
  }

  connectedCallback() {
    // console.log('CustomAvisList connected');
    this.init();
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.searchInput) {
      this.searchInput.removeEventListener('input', this.handleSearchInputBound);
    }
    if (this.searchClearButton) {
      this.searchClearButton.removeEventListener('click', this.clearSearchBound);
    }
    window.removeEventListener('resize', this.handleResizeBound);
  }

  init() {
    // Bind event handlers to preserve 'this' context
    this.handleSearchInputBound = this.handleSearchInput.bind(this);
    this.clearSearchBound = this.clearSearch.bind(this);
    this.handleResizeBound = this.debounce(this.handleResize.bind(this), 100);

    // Set up the DOM structure
    this.setupDOM();

    // Add event listeners
    this.addEventListeners();

    // Check if search parameter exists in URL
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q');

    if (searchQuery) {
      // Initialize in search mode with the query from URL
      // console.log(`Initializing in search mode with query: ${searchQuery}`);
      this.searchTerm = searchQuery;

      // Update the search input field with the query
      if (this.searchInput) {
        this.searchInput.value = searchQuery;
        this.searchClearButton.style.display = 'block';
      }

      // Load search results
      this.loadAvisSearch();
    } else {
      // Load initial data in normal mode
      this.loadAvisList();
    }
  }

  debounce(func, wait) {
    let timeout;
    return function (...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }

  getPageFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const page = parseInt(urlParams.get('page'));
    return !isNaN(page) && page > 0 ? page : 1;
  }

  setupDOM() {
    // Find containers
    this.contentContainer = this.querySelector('[data-custom-avis-list-container]');
    this.paginationContainer = this.querySelector('[data-custom-avis-list-pagination]');

    if (!this.contentContainer) {
      console.error('Content container not found');
      return;
    }

    // Clear content container
    this.contentContainer.innerHTML = '';

    // Find existing search input and clear button instead of creating new ones
    this.searchInput = this.querySelector('[data-custom-avis-list-input]');
    this.searchClearButton = this.querySelector('[data-custom-avis-list-clear]');

    // Create loading spinner
    this.loadingSpinner = document.createElement('div');
    this.loadingSpinner.className = 'tw-flex tw-justify-center tw-items-center tw-py-8 tw-w-full';
    this.loadingSpinner.innerHTML = `<svg class="spinner tw-h-8 tw-w-8" aria-hidden="true" focusable="false" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
    </svg>
    `;
    this.loadingSpinner.style.display = 'none';

    // Create grid container
    this.gridContainer = document.createElement('div');
    this.gridContainer.className = 'avis-list__grid';

    // Append elements to content container (excluding search elements which already exist in the markup)
    this.contentContainer.appendChild(this.loadingSpinner);
    this.contentContainer.appendChild(this.gridContainer);
  }

  addEventListeners() {
    // Add search event listeners
    if (this.searchInput && this.searchClearButton) {
      this.searchInput.addEventListener('input', this.handleSearchInputBound);
      this.searchClearButton.addEventListener('click', this.clearSearchBound);
    }

    // Add window resize listener
    window.addEventListener('resize', this.handleResizeBound);
  }

  handleResize() {
    // Use requestAnimationFrame to ensure the resize happens during the next paint cycle
    // This prevents layout thrashing and visual glitches
    if (this.resizeRAF) {
      cancelAnimationFrame(this.resizeRAF);
    }

    this.resizeRAF = requestAnimationFrame(() => {
      if (this.gridContainer) {
        // Force a repaint by temporarily hiding the grid
        this.gridContainer.style.visibility = 'hidden';

        // Force a reflow
        void this.gridContainer.offsetHeight;

        // Make visible again
        this.gridContainer.style.visibility = 'visible';
      }
    });
  }

  handleSearchInput() {
    const value = this.searchInput.value.trim();

    if (value) {
      this.searchClearButton.style.opacity = 1;
    } else {
      this.searchClearButton.style.opacity = 0;
    }

    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.searchTerm = value;

      if (this.searchTerm) {
        // Use search endpoint when there's a search term
        this.loadAvisSearch();
      } else {
        // Use list endpoint when there's no search term
        this.loadAvisList();
      }
    }, 300);
  }

  clearSearch() {
    if (!this.searchInput) return;

    this.searchInput.value = '';
    this.searchClearButton.style.display = 'none';
    this.searchTerm = '';
    this.loadAvisList();
  }

  showLoading(show) {
    if (this.loadingSpinner) {
      this.loadingSpinner.style.display = show ? 'flex' : 'none';
    }

    if (this.gridContainer) {
      this.gridContainer.style.opacity = show ? '0.5' : '1';
    }
  }

  loadAvisList() {
    if (!this.contentContainer || !this.gridContainer) {
      console.error('Content or grid container not found');
      return;
    }

    this.isLoading = true;
    this.showLoading(true);

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    const page = this.currentPage;

    let developmentModeUrl = window?.developmentMode?.url ? `${window?.developmentMode?.url}/api/shopify/avis/list?page=${page}&limit=${this.itemsPerPage}` : `https://yves-legare-server.vercel.app/api/shopify/avis/list?page=${page}&limit=${this.itemsPerPage}`;
    let developmentModeEnabled = window?.developmentMode?.enabled;
    let url = developmentModeEnabled ? developmentModeUrl : `https://yves-legare-server.vercel.app/api/shopify/avis/list?page=${page}&limit=${this.itemsPerPage}`;
    let targetStoreHeader = window?.targetStore?.enabled ? window?.targetStore?.value : null;
    
    let fetchOptions = {
      signal: this.abortController.signal
    };

    if (targetStoreHeader) {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        'x-target-store': targetStoreHeader
      };
    }

    // console.log(`Fetching avis data from API for page ${page}...`);

    fetch(url, {
      ...fetchOptions
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {

        if (!data?.results || !Array.isArray(data.results) || data.results.length === 0) {
          console.error('Invalid or empty data received');
          this.allAvisItems = [];
          this.totalItems = this.allAvisItems.length;
          this.pagination = {
            currentPage: 1,
            pageSize: this.itemsPerPage,
            hasNextPage: false
          };
        } else {
          this.allAvisItems = data?.results || [];
          this.totalItems = data.pagination?.total || this.allAvisItems.length;
          this.pagination = data.pagination || {
            currentPage: this.currentPage,
            pageSize: this.itemsPerPage,
            hasNextPage: this.allAvisItems.length >= this.itemsPerPage
          };
        }

        this.filteredAvisItems = [...this.allAvisItems];
        this.renderAvisList();
        this.generateAvisPagination();
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Error fetching avis data:', error);
          this.allAvisItems = [];
          this.totalItems = this.allAvisItems.length;
          this.pagination = {
            currentPage: 1,
            pageSize: this.itemsPerPage,
            hasNextPage: false
          };
          this.filteredAvisItems = [...this.allAvisItems];
          this.renderAvisList();
          this.generateAvisPagination();
        }
      })
      .finally(() => {
        this.isLoading = false;
        this.showLoading(false);
      });
  }

  loadAvisSearch() {
    if (!this.contentContainer || !this.gridContainer) {
      console.error('Content or grid container not found');
      return;
    }

    this.isLoading = true;
    this.showLoading(true);

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    // console.log(`Searching avis with query: ${this.searchTerm}`);

    let developmentModeUrl = window?.developmentMode?.url ? `${window?.developmentMode?.url}/api/shopify/avis/search?q=${encodeURIComponent(this.searchTerm)}` : `https://yves-legare-server.vercel.app/api/shopify/avis/search?q=${encodeURIComponent(this.searchTerm)}`;
    let developmentModeEnabled = window?.developmentMode?.enabled;
    let url = developmentModeEnabled ? developmentModeUrl : `https://yves-legare-server.vercel.app/api/shopify/avis/search?q=${encodeURIComponent(this.searchTerm)}`;
    let targetStoreHeader = window.targetStore;

    let fetchOptions = {
      signal: this.abortController.signal
    };

    if (targetStoreHeader) {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        'x-target-store': targetStoreHeader
      };
    }


    fetch(url, {
      signal: this.abortController.signal,
      fetchOptions
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        // console.log('Search results received:', data);

        if (!data?.results || !Array.isArray(data.results) || data.results.length === 0) {
          console.error('Invalid or empty search results');
          this.allAvisItems = [];
          this.filteredAvisItems = [];
        } else {
          this.allAvisItems = data.results;
          this.filteredAvisItems = [...this.allAvisItems];
        }

        // Reset to first page for search results
        this.currentPage = 1;

        this.renderAvisList();
        this.generateAvisPagination();
      })
      .catch(error => {
        if (error.name !== 'AbortError') {
          console.error('Error searching avis data:', error);
          this.allAvisItems = [];
          this.filteredAvisItems = [];
          this.renderAvisList();
          this.generateAvisPagination();
        }
      })
      .finally(() => {
        this.isLoading = false;
        this.showLoading(false);
      });
  }
  
  renderAvisList() {
    if (!this.gridContainer) {
      console.error('Grid container not found');
      return;
    }

    // console.log('Rendering avis list');

    // Clear the grid container
    this.gridContainer.innerHTML = '';

    let itemsToShow = this.filteredAvisItems;

    // For search results, we might need to handle pagination client-side
    // if the search endpoint doesn't support pagination
    if (this.searchTerm && this.filteredAvisItems.length > this.itemsPerPage) {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      itemsToShow = this.filteredAvisItems.slice(startIndex, endIndex);
    }

    // console.log(`Showing ${itemsToShow.length} items, total filtered: ${this.filteredAvisItems.length}`);

    if (itemsToShow.length === 0) {
      const noResultsElement = document.createElement('div');
      noResultsElement.className = 'tw-text-center tw-py-8 tw-w-full';
      noResultsElement.textContent = 'Aucun avis trouvé.';
      this.gridContainer.appendChild(noResultsElement);
      return;
    }

    // Create fragment to improve performance
    const fragment = document.createDocumentFragment();

    // Append items to the fragment
    itemsToShow.forEach(avis => {
      const avisElement = this.createAvisElement(avis);
      fragment.appendChild(avisElement);
    });

    // Append fragment to grid container
    this.gridContainer.appendChild(fragment);

    // Ensure all images are loaded before showing the grid
    this.waitForImagesToLoad().then(() => {
      this.gridContainer.style.opacity = '1';
    });
  }

  // Wait for all images in the grid to load
  waitForImagesToLoad() {
    return new Promise(resolve => {
      const images = this.gridContainer.querySelectorAll('img');

      if (images.length === 0) {
        resolve();
        return;
      }

      let loadedImagesCount = 0;
      const totalImagesCount = images.length;

      const imageLoaded = () => {
        loadedImagesCount++;
        if (loadedImagesCount === totalImagesCount) {
          // Add a small delay to ensure browser has time to process the layout
          setTimeout(() => {
            resolve();
          }, 50);
        }
      };

      images.forEach(img => {
        // For already loaded images
        if (img.complete) {
          imageLoaded();
        } else {
          // For images still loading
          img.addEventListener('load', imageLoaded, { once: true });
          img.addEventListener('error', imageLoaded, { once: true }); // Count errors as loaded
        }
      });

      // Safety timeout in case some images never load
      setTimeout(() => {
        if (loadedImagesCount < totalImagesCount) {
          console.warn(`Not all images loaded (${loadedImagesCount}/${totalImagesCount}), resolving anyway`);
          resolve();
        }
      }, 3000);
    });
  }

  generateAvisPagination() {
    if (!this.paginationContainer) {
      console.error('Pagination container not found');
      return;
    }

    // console.log('Generating pagination');

    // Clear pagination container
    this.paginationContainer.innerHTML = '';

    // Don't show pagination if there are no results
    if (this.filteredAvisItems.length === 0) {
      // console.log('No pagination needed - no results');
      return;
    }

    // For search results, calculate pagination info
    let currentPage, hasNextPage;

    if (this.searchTerm) {
      // Client-side pagination for search results
      currentPage = this.currentPage;
      const totalPages = Math.ceil(this.filteredAvisItems.length / this.itemsPerPage);
      hasNextPage = currentPage < totalPages;
    } else {
      // Server-side pagination for list
      currentPage = this.pagination?.currentPage || this.currentPage;
      hasNextPage = this.pagination?.hasNextPage || false;
    }

    // console.log(`Generating pagination: currentPage=${currentPage}, hasNextPage=${hasNextPage}`);

    // Create pagination wrapper
    const paginationWrapper = document.createElement('div');
    paginationWrapper.className = 'pagination-wrapper';

    // Create pagination nav
    const paginationNav = document.createElement('nav');
    paginationNav.className = 'pagination';
    paginationNav.setAttribute('role', 'navigation');
    paginationNav.setAttribute('aria-label', 'Pagination');

    // Create pagination list
    const paginationList = document.createElement('ul');
    paginationList.className = 'pagination__list list-unstyled';
    paginationList.setAttribute('role', 'list');

    // Previous button - only show if not on first page
    if (currentPage > 1) {
      const prevItem = document.createElement('li');
      const prevLink = document.createElement('a');
      prevLink.href = this.getPaginationUrl(currentPage - 1);
      prevLink.className = 'pagination__item--prev pagination__item-arrow link motion-reduce';
      prevLink.setAttribute('aria-label', window.themeVariables.strings.paginationPrev);
      prevLink.title = window.themeVariables.strings.paginationPrev;
      prevLink.innerHTML = `<svg width="16" height="16" style="transform: rotate(180deg)" viewBox="0 0 16 16" class="icon icon-caret" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

      // For search results, handle pagination client-side
      if (this.searchTerm) {
        prevLink.addEventListener('click', (e) => {
          e.preventDefault();
          this.currentPage--;
          this.renderAvisList();
          this.generateAvisPagination();
        });
      }

      prevItem.appendChild(prevLink);
      paginationList.appendChild(prevItem);
    }

    // Current page
    const currentPageItem = document.createElement('li');
    const currentSpan = document.createElement('span');
    currentSpan.className = 'pagination__item pagination__item--current';
    currentSpan.setAttribute('aria-current', 'page');
    currentSpan.setAttribute('aria-label', `Page ${currentPage}`);
    currentSpan.textContent = currentPage;
    currentPageItem.appendChild(currentSpan);
    paginationList.appendChild(currentPageItem);

    // Next button - only show if hasNextPage is true
    if (hasNextPage) {
      const nextItem = document.createElement('li');
      const nextLink = document.createElement('a');
      nextLink.href = this.getPaginationUrl(currentPage + 1);
      nextLink.className = 'pagination__item--next pagination__item-arrow link motion-reduce';
      nextLink.setAttribute('aria-label', window.themeVariables.strings.paginationNext);
      nextLink.title = window.themeVariables.strings.paginationNext;
      nextLink.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" class="icon icon-caret" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>`;

      // For search results, handle pagination client-side
      if (this.searchTerm) {
        nextLink.addEventListener('click', (e) => {
          e.preventDefault();
          this.currentPage++;
          this.renderAvisList();
          this.generateAvisPagination();
        });
      }

      nextItem.appendChild(nextLink);
      paginationList.appendChild(nextItem);
    }

    // Assemble pagination
    paginationNav.appendChild(paginationList);
    paginationWrapper.appendChild(paginationNav);
    this.paginationContainer.appendChild(paginationWrapper);
  }

  getPaginationUrl(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', page);
    return url.toString();
  }

  createAvisElement(avis) {

    // console.log(avis, 'CREATE AVIS')

    const avisElement = document.createElement('div');
    avisElement.classList.add('avis-item');
    avisElement.setAttribute('data-avis-id', avis.id);

    const firstName = avis.firstName || '';
    const lastName = avis.lastName || '';
    const MaidenName = avis.MaidenName || '';
    const birthDate = avis.birthDate || '';
    const deathDate = avis.deathDate || '';

    const birthYear = birthDate ? birthDate.split('-')[0] : '';
    const deathYear = deathDate ? deathDate.split('-')[0] : '';

    const matchingAvis = window.avis && window.avis.find(
      (item) => item.unique_id === avis.contractId
    );

    const imageUrl = !avis.error ? avis.image?.url : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg);
    // const imageUrl = matchingAvis ? matchingAvis.avis_image : (window.themeVariables.avisImagePlaceholder ?? window.avisSvg);
    const avisUrl = matchingAvis ? matchingAvis.avis_url : `/pages/avis-de-deces/${avis.handle || ''}`;
    const placeholderImage = window.themeVariables.avisImagePlaceholder ?? window.avisSvg;
    let fullName = '';

    if (MaidenName != '' && lastName != '') {
      fullName = `<span>${firstName} ${lastName} (née ${MaidenName})</span>`;
    } else if (lastName === '' && MaidenName != '') {
      fullName = `<span>${firstName} ${MaidenName}</span>`;
    } else if (lastName != '' && firstName != '') {
      fullName = `<span>${firstName} ${lastName}</span>`;
    }

    avisElement.innerHTML = `
      <div class="avis-marquee-card">
        <a href="${avisUrl}" class="avis-marquee-card__content">
          <div class="avis-marquee-card__content-image-wrapper tw-relative">
            ${imageUrl ? 
              `<img
                src="${imageUrl}"
                alt="${firstName} ${lastName}"
                width="304"
                height="296"
                class="avis-marquee-card__img tw-filter tw-grayscale grey-scale"
                loading="lazy"
              >` : 
              `<div class="avis-marquee-card__img-placeholder">
                <img
                  src="${placeholderImage}"
                  alt=""
                  width="400"
                  height="390"
                  class="avis-marquee-card__img tw-filter tw-grayscale grey-scale"
                  loading="lazy"
                >
              </div>`
            }
            <div class="avis__quick-view tw-absolute tw-bottom-4 tw-left-4 tw-z-10">
              <svg class="avis-marquee-card__icon-eye" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5.25C4.5 5.25 1.5 12 1.5 12C1.5 12 4.5 18.75 12 18.75C19.5 18.75 22.5 12 22.5 12C22.5 12 19.5 5.25 12 5.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
          <div class="avis-marquee-card__content-body tw-flex tw-flex-col">
            <div class="tw-flex tw-flex-wrap tw-gap-2">
              <h5 class="tw-font-bold tw-mt-4 tw-text-left tw-capitalize tw-font-heading">
                ${fullName}
              </h5>
            </div>
            <div class="tw-flex tw-gap-1">
              ${birthYear ? `<p>${birthYear}</p><span>-</span>` : ''}
              ${deathYear ? `<p>${deathYear}</p>` : ''}
            </div>
          </div>
        </a>
      </div>
    `;

    return avisElement;
  }
}

if (!window.customElements.get('custom-avis-list')) {
  window.customElements.define('custom-avis-list', CustomAvisList);
}